import { Box, Card, CardContent, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import MedicationIcon from '@mui/icons-material/Medication';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { EmptyState } from '@/components/EmptyState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { patient } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { Prescription, PrescriptionStatus } from '@/types/medical';

const ACTIVE: PrescriptionStatus[] = ['signed', 'registered', 'partly_dispensed'];
const HISTORY: PrescriptionStatus[] = ['dispensed', 'expired', 'cancelled', 'failed'];

type TabKey = 'active' | 'history';

export function PatientPrescriptionsPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'prescriptions'],
    queryFn:  patient.prescriptions.list,
  });

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    return {
      active:  all.filter((r) => ACTIVE.includes(r.status))
                  .sort((a, b) => new Date(b.issued_at ?? 0).getTime() - new Date(a.issued_at ?? 0).getTime()),
      history: all.filter((r) => HISTORY.includes(r.status))
                  .sort((a, b) => new Date(b.issued_at ?? 0).getTime() - new Date(a.issued_at ?? 0).getTime()),
    };
  }, [q.data]);

  const initial: TabKey = buckets.active.length > 0 || buckets.history.length === 0
    ? 'active' : 'history';
  const [tab, setTab] = useState<TabKey>(initial);

  return (
    <>
      <PageHeader
        title="My prescriptions"
        subtitle="Active prescriptions can be filled at any pharmacy. Expired and cancelled ones are kept for your records."
      />

      <DataState query={q}>
        {() => (
          <>
            <Tabs
              value={tab} onChange={(_, v: TabKey) => setTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="active"  label={`Active (${buckets.active.length})`} />
              <Tab value="history" label={`History (${buckets.history.length})`} />
            </Tabs>
            {buckets[tab].length === 0 ? (
              <EmptyState
                title={tab === 'active' ? 'No active prescriptions' : 'No prescription history yet'}
                description={tab === 'active' ? 'Your clinician will issue prescriptions during a consultation.' : undefined}
              />
            ) : (
              <Stack spacing={1.5}>
                {buckets[tab].map((r) => <PrescriptionCard key={r.id} rx={r} />)}
              </Stack>
            )}
          </>
        )}
      </DataState>
    </>
  );
}

function PrescriptionCard({ rx }: { rx: Prescription }) {
  const items = rx.items ?? [];
  const head = items[0];
  return (
    <Card
      component={RouterLink}
      to={`/patient/prescriptions/${rx.id}`}
      sx={{
        textDecoration: 'none', color: 'inherit',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40,
            borderRadius: 1.5, bgcolor: 'background.subtle',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'primary.main', flexShrink: 0,
          }}>
            <MedicationIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {head?.drug_name_snapshot ?? 'Prescription'}
                {items.length > 1 && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}+ {items.length - 1} more
                  </Typography>
                )}
              </Typography>
              <StatusChip status={rx.status} />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {rx.clinician_name} · {rx.jurisdiction_code}
              {rx.issued_at && ` · issued ${formatDateTime(rx.issued_at)}`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" className="tabular">
              {rx.valid_until ? `valid until ${formatDate(rx.valid_until)}` : '—'}
            </Typography>
          </Box>
          <ChevronRightIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatusChip({ status }: { status: PrescriptionStatus }) {
  const config: Record<PrescriptionStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    draft:           { label: 'draft', color: 'default' },
    signed:          { label: 'active', color: 'primary' },
    registered:      { label: 'registered', color: 'primary' },
    dispensed:       { label: 'dispensed', color: 'success' },
    partly_dispensed:{ label: 'partly filled', color: 'primary' },
    cancelled:       { label: 'cancelled', color: 'default' },
    expired:         { label: 'expired', color: 'default' },
    failed:          { label: 'failed', color: 'error' },
  };
  const c = config[status];
  return <Chip size="small" label={c.label} color={c.color} variant="outlined" />;
}
